import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  // Allow message to be a React node so callers can render forms or rich content
  message: React.ReactNode;
  note?: string; // Optional note/warning message displayed separately
  type?: 'success' | 'error' | 'warning' | 'info';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  showCancel?: boolean;
  // Optional custom actions area to fully control buttons/links
  customActions?: React.ReactNode;
  // Optional size override to prevent automatic width switching
  size?: 'compact' | 'extended' | 'auto';
}

const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  message,
  note,
  type = 'info',
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm,
  showCancel = false,
  customActions,
  size = 'auto',
}) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getIconAndColors = () => {
    switch (type) {
      case 'success':
        return {
          icon: (
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          iconBg: 'bg-green-100',
        };
      case 'error':
        return {
          icon: (
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ),
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          iconBg: 'bg-red-100',
        };
      case 'warning':
        return {
          icon: (
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          ),
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          iconBg: 'bg-yellow-100',
        };
      default:
        return {
          icon: (
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200',
          iconBg: 'bg-purple-100',
        };
    }
  };

  const { icon, bgColor, borderColor, iconBg } = getIconAndColors();

  // Determine dialog width based on size prop or content heuristic
  const getDialogWidth = () => {
    if (size === 'compact') return 'max-w-md';
    if (size === 'extended') return 'max-w-2xl';
    
    // Auto mode: compute approximate text length from message ReactNode to decide compact vs extended width
    const getNodeTextLength = (node: React.ReactNode): number => {
      if (node == null) return 0;
      if (typeof node === 'string' || typeof node === 'number') return String(node).length;
      if (Array.isArray(node)) return node.reduce((sum, child) => sum + getNodeTextLength(child), 0);
      if (React.isValidElement(node)) return getNodeTextLength((node as React.ReactElement<{ children?: React.ReactNode }>).props?.children);
      return 0;
    };

    const messageLength = getNodeTextLength(message);
    const isCompact = messageLength > 0 && messageLength <= 120;
    return isCompact ? 'max-w-md' : 'max-w-2xl';
  };

  const dialogWidth = getDialogWidth();

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      onClose();
    }
  };

  const dialogContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-3 sm:p-4 relative z-10">
        <div className={`relative w-full ${dialogWidth} max-h-[calc(100dvh-1.5rem)] transform overflow-y-auto rounded-lg bg-white shadow-2xl transition-all`}>
          {/* Header */}
          <div className={`${bgColor} ${borderColor} border-b px-4 py-4 sm:px-6`}>
            <div className="flex items-center space-x-3">
              <div className={`flex-shrink-0 rounded-full p-2 ${iconBg}`}>
                {icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {title}
              </h3>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:px-6 sm:py-6">
            <div className="text-gray-700 leading-relaxed text-sm whitespace-pre-line">
              {message}
            </div>
            {note && (
              <div className="mt-4 text-sm text-gray-700 bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                <span className="font-medium text-yellow-800">Note:</span>{" "}
                {note}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse gap-2 bg-gray-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
            {customActions ? (
              customActions
            ) : (
              <>
                {showCancel && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:ring-offset-1 transition-colors"
                  >
                    {cancelText}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="rounded-lg bg-purple-800 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:ring-offset-1 transition-colors"
                >
                  {confirmText}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(dialogContent, document.body);
};

export default Dialog;
